/* eslint-disable no-param-reassign */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { denormalize, normalize } from 'normalizr'
import { Selector } from 'react-redux'
import { PaginatedRequestState } from 'src/domain/request'
import {
  NormalizedStoreSliceReportSummary,
  storeSalesReportSummaryEntity,
} from 'src/domain/schema'
import { Store, StoreSalesReportSummary } from 'src/slices/services/api'
import AuthenticatedApi from 'src/slices/services/authenticatedApi'
import serializeError from 'src/slices/services/error'
// eslint-disable-next-line import/no-cycle
import { AppThunkConfig, RootState } from 'src/store'
import { purge } from 'src/store/action'
import toKey from 'src/utils/key'

interface StoreSalesReportSummariesState
  extends EntityState<NormalizedStoreSliceReportSummary> {
  query: {
    [code: string]: PaginatedRequestState
  }
}

const storeSalesReportSummariesAdapter =
  createEntityAdapter<NormalizedStoreSliceReportSummary>({
    selectId: (summary) => summary.storeIdDate,
  })

export const initialState: StoreSalesReportSummariesState =
  storeSalesReportSummariesAdapter.getInitialState({
    query: {},
  })

type GetStoreSalesReportSummariesParams = {
  orgCode: string
  date: string
  q?: string
}

interface GetStoreSalesReportSummariesEntities {
  storeSalesReportSummaries: Record<string, NormalizedStoreSliceReportSummary>
  stores: Record<string, Store>
}

interface GetStoreSalesReportSummariesReturned {
  entities: GetStoreSalesReportSummariesEntities
}

export const getStoreSalesReportSummaries = createAsyncThunk<
  GetStoreSalesReportSummariesReturned,
  GetStoreSalesReportSummariesParams,
  AppThunkConfig
>(
  'storeSalesReportSummaries/getStoreSalesReportSummaries',
  async (params, { getState }) => {
    const { auth } = getState()
    const response = await new AuthenticatedApi(
      auth.token
    ).getOrganizationsOrganizationCodeStoreSalesReportSummaries(
      params.orgCode,
      params.date,
      params.date,
      params.q
    )

    const mappedResponse = response.data.map((data) => {
      const date = dayjs(data.date).format('YYYY-MM-DD')
      return {
        ...data,
        storeIdDate: `${data.store.id}-${date}`,
      }
    })

    const normalized = normalize<
      NormalizedStoreSliceReportSummary,
      GetStoreSalesReportSummariesEntities
    >(mappedResponse, [storeSalesReportSummaryEntity])

    return {
      entities: normalized.entities,
    }
  },
  { serializeError }
)

const slice = createSlice({
  name: 'storeSalesReportSummaries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(purge, () => {
      return initialState
    })
    builder.addCase(getStoreSalesReportSummaries.pending, (state, { meta }) => {
      return {
        ...state,
        query: {
          ...state.query,
          [toKey(meta.arg)]: {
            status: 'loading',
          },
        },
      }
    })
    builder.addCase(
      getStoreSalesReportSummaries.fulfilled,
      (state, { payload, meta }) => {
        const { entities } = payload
        const key = toKey(meta.arg)

        if (entities.storeSalesReportSummaries === undefined) {
          state.query[key] = {
            status: 'succeeded',
          }
          return
        }

        storeSalesReportSummariesAdapter.upsertMany(
          state,
          entities.storeSalesReportSummaries ?? {}
        )

        state.query[key] = {
          status: 'succeeded',
          ids: Object.keys(entities.storeSalesReportSummaries),
        }
      }
    )
    builder.addCase(
      getStoreSalesReportSummaries.rejected,
      (state, { error, meta }) => {
        return {
          ...state,
          query: {
            ...state.query,
            [toKey(meta.arg)]: {
              status: 'failed',
              error,
            },
          },
        }
      }
    )
  },
})

export default slice.reducer

export const {
  selectById: selectStoreSalesReportSummaryById,
  selectEntities: selectStoreSalesReportSummaryEntities,
  selectAll: selectAllStoreSalesReportSummaries,
} = storeSalesReportSummariesAdapter.getSelectors<StoreSalesReportSummariesState>(
  (state) => state
)

export const selectStoreSalesReportSummariesByParams = (
  params: GetStoreSalesReportSummariesParams
): Selector<RootState, NormalizedStoreSliceReportSummary[] | undefined> => {
  const key = toKey(params)
  return createSelector(
    [(state) => state.entities.storeSalesReportSummaries],
    (state) => {
      if (!state.query[key]) {
        return undefined
      }
      const entities = selectStoreSalesReportSummaryEntities(state)
      /* eslint-disable @typescript-eslint/no-explicit-any */
      return state.query[key].ids
        ?.map((id: any) => entities[id])
        .filter(
          (entity: any): entity is NormalizedStoreSliceReportSummary =>
            entity !== undefined
        )
      /* eslint-enable */
    }
  )
}

export const selectStoreSalesReportSummariesRequestStateByParams = (
  params: GetStoreSalesReportSummariesParams
): Selector<RootState, PaginatedRequestState> => {
  return createSelector(
    [(state) => state.entities.storeSalesReportSummaries],
    (state) => {
      return (
        state.query[toKey(params)] ?? {
          status: 'idle',
        }
      )
    }
  )
}

export const selectDenormalizedStoreSalesReportSummariesByParams = (
  params: GetStoreSalesReportSummariesParams
): Selector<RootState, StoreSalesReportSummary[] | undefined> => {
  return createSelector(
    [
      (state) => state.entities.storeSalesReportSummaries,
      (state) => state.entities.stores,
    ],
    (state, stores) => {
      const ids = state.query[toKey(params)]?.ids
      if (!ids) {
        return undefined
      }
      return denormalize(ids, [storeSalesReportSummaryEntity], {
        storeSalesReportSummaries: state.entities,
        stores: stores.entities,
      })
    }
  )
}
